import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { slugify } from './news';

export { slugify };

const COLLECTION = 'tournaments';

// Create a new tournament document. Stamps createdAt/updatedAt.
export const createTournament = async (data) => {
  const now = Timestamp.now();
  const ref = await addDoc(collection(db, COLLECTION), {
    ...data,
    createdAt: now,
    updatedAt: now,
  });
  return ref.id;
};

// Partial update of a tournament document. Stamps updatedAt.
export const updateTournament = (id, data) =>
  updateDoc(doc(db, COLLECTION, id), { ...data, updatedAt: Timestamp.now() });

// Mapping-format voting controls (read defaults: open unless === false,
// average hidden unless === true — see MappingTournamentView / firestore.rules).
export const setVotingOpen = (id, open) =>
  updateDoc(doc(db, COLLECTION, id), { votingOpen: open, updatedAt: Timestamp.now() });

export const setCommunityAverageVisible = (id, visible) =>
  updateDoc(doc(db, COLLECTION, id), {
    communityAverageVisible: visible,
    updatedAt: Timestamp.now(),
  });

// Live tournament doc ({ id, ...data } or null). Returns the unsubscribe function.
export const subscribeTournament = (id, onData, onError) =>
  onSnapshot(
    doc(db, COLLECTION, id),
    (snap) => onData(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    onError
  );

// Staged format: show or hide one public section (see PUBLIC_SECTIONS).
export const setSectionVisibility = (id, key, visible) =>
  updateDoc(doc(db, COLLECTION, id), {
    [`visibility.${key}`]: visible,
    updatedAt: Timestamp.now(),
  });

// True if `slug` is already used by a tournament other than `exceptId`.
export const isTournamentSlugTaken = async (slug, exceptId = null) => {
  if (!slug) return false;
  const q = query(collection(db, COLLECTION), where('slug', '==', slug), limit(2));
  const snap = await getDocs(q);
  return snap.docs.some((d) => d.id !== exceptId);
};

export const getTournamentBySlug = async (slug) => {
  if (!slug) return null;
  const q = query(collection(db, COLLECTION), where('slug', '==', slug), limit(1));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
};

export const getTournamentById = async (id) => {
  if (!id) return null;
  const snap = await getDoc(doc(db, COLLECTION, id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

// Returns `base`, or `base-2`, `base-3`, … — whichever is first not in `taken`.
export const uniqueSlug = (base, taken) => {
  const root = base || 'tournament';
  let candidate = root;
  let i = 2;
  while (taken.has(candidate)) {
    candidate = `${root}-${i}`;
    i += 1;
  }
  return candidate;
};

// One-time backfill: give every tournament without a slug one derived from its
// name, keeping all slugs unique. Returns the number of tournaments updated.
export const backfillTournamentSlugs = async () => {
  const snap = await getDocs(collection(db, COLLECTION));
  const taken = new Set();
  snap.docs.forEach((d) => {
    const s = d.data().slug;
    if (s) taken.add(s);
  });

  let updated = 0;
  for (const d of snap.docs) {
    if (d.data().slug) continue;
    const slug = uniqueSlug(slugify(d.data().name) || d.id, taken);
    taken.add(slug);
    // eslint-disable-next-line no-await-in-loop
    await updateDoc(doc(db, COLLECTION, d.id), { slug });
    updated += 1;
  }
  return updated;
};
